import React, { useCallback } from 'react';
import {
  Box,
  Row,
  Column,
  GridContainer,
  FlexBox,
  Divider,
  H4,
} from '@vp/swan';
import { useTranslations, ReplaceTokens } from '@vp/om-translations';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { ModTarget } from '@vp/mod-father';
import IfDebug from '../debugTooling/IfDebug';
import { CartItemDebugDetails } from '../debugTooling/CartItemDebugDetails';
import { cartItemSelectors, cartItemOperations } from '../../state/cartItems';
import QuantitySelector from '../QuantitySelector';
import CartItemAttributeSummary from './CartItemAttributeSummary';
import { ConfirmationModal } from '../ConfirmationModal';
import { CustomDataDisplay } from './CustomDataDisplay';
import { ItemPricingSummary } from './ItemPricingSummary';
import { pricingSelectors } from '../../state/pricing';
import useTracking from '../../hooks/useTracking';
import { ModWrapperWithPadding } from '../uxMods/ModWrapperWithPadding';
import useIdentityContext from '../../hooks/useIdentityContext';
import useSiteData from '../../hooks/useSiteData';
import useExperiment from '../../hooks/useExperiment';
import {
  trackSaveForLaterClicks,
  trackCancelSaveForLaterClicks,
  trackOpenSaveForLaterClicks,
} from '../../apis/newRelicTracking';
import { DesignPreview } from '../DesignPreview';

const ModColumnWrapper = ({ children }) => (
  <Column span={12}>
    <Box mb="3">{children}</Box>
  </Column>
);
ModColumnWrapper.propTypes = {
  children: PropTypes.element,
};

// Consider adding a Grid Container around this so that this component is generic enough to live on its own
export const CartItem = ({ cartItemId, supportedActions = {} }) => {
  const { externalAppNavigation } = useSiteData();
  const { identity } = useIdentityContext();

  // default to supporting all actions
  const {
    remove: supportRemove = true,
    updateQuantity: supportUpdateQuantity = true,
  } = supportedActions;

  const [cartMessages] = useTranslations(['cartMessages']);
  const pricingData = useSelector(state =>
    pricingSelectors.selectPricingDataByItemId(state.pricing, cartItemId)
  );
  const hasDocument = useSelector(state =>
    cartItemSelectors.selectDocumentRefUrl(state.cartItems, cartItemId)
  );
  const productName = useSelector(state =>
    cartItemSelectors.selectProductNameByItemId(state.cartItems, cartItemId)
  );
  const customDisplayAttributes = useSelector(state =>
    cartItemSelectors.selectCustomDisplayAttributesByItemId(
      state.cartItems,
      cartItemId
    )
  );
  const quantity = useSelector(state =>
    cartItemSelectors.selectQuantityByItemId(state.cartItems, cartItemId)
  );
  const merchandisableQuantities = useSelector(state =>
    cartItemSelectors.selectMerchandisableQuantitiesV2(
      state.cartItems,
      cartItemId
    )
  );
  const productAttributes = useSelector(state =>
    cartItemSelectors.selectProductAttributes(state.cartItems, cartItemId)
  );
  const blockUserInteraction = useSelector(state =>
    cartItemSelectors.selectBlockUserInteractionByItemId(
      state.cartItems,
      cartItemId
    )
  );

  const tracker = useTracking();

  const dispatch = useDispatch();

  const supportSaveForLater =
    supportRemove && !!hasDocument && identity.isSignedIn;

  const shouldDisplaySaveForLaterAction = useExperiment(
    'save_for_later',
    'variation_2',
    supportSaveForLater
  );

  const removeItem = useCallback(
    () => dispatch(cartItemOperations.remove(cartItemId)),
    [cartItemId, dispatch]
  );

  const onQuantityChange = newValue =>
    dispatch(
      cartItemOperations.updateQuantity({
        itemId: cartItemId,
        quantityToUpdateTo: newValue,
        originalQuantity: quantity,
      })
    );

  const removeItemWarning = !hasDocument
    ? cartMessages.removeItemWarning
    : cartMessages.removeItemWarningWithMyProjects;
  const removeItemVisuallyHiddenLabel = (
    <ReplaceTokens
      tokenizedString={cartMessages.removeProductFromCart}
      substitutions={{ productName }}
    />
  );

  const handleSaveForLaterClick = useCallback(() => {
    trackSaveForLaterClicks(cartItemId);
    tracker.trackExperimentClicked(
      externalAppNavigation.cart,
      externalAppNavigation.cart,
      'saveForLaterConfirm',
      'save_for_later',
      'variation_2',
      cartItemId
    );
    removeItem();
  }, [cartItemId, tracker, externalAppNavigation.cart, removeItem]);

  const handleCancelSaveForLater = useCallback(() => {
    trackCancelSaveForLaterClicks(cartItemId);
    tracker.trackExperimentClicked(
      externalAppNavigation.cart,
      externalAppNavigation.cart,
      'saveForLaterDecline',
      'save_for_later',
      'variation_2',
      cartItemId
    );
  }, [cartItemId, tracker, externalAppNavigation.cart]);

  const handleOpenSaveForLater = useCallback(() => {
    trackOpenSaveForLaterClicks(cartItemId);
    tracker.trackExperimentClicked(
      externalAppNavigation.cart,
      externalAppNavigation.cart,
      'saveForLater',
      'save_for_later',
      'variation_2',
      cartItemId
    );
  }, [cartItemId, tracker, externalAppNavigation.cart]);

  return (
    <Row data-testid={`cart-item-${cartItemId}`}>
      <Column span={3} spanXs={3}>
        <GridContainer>
          <Row marginBottom="2">
            <Column span={12}>
              <IfDebug>
                <CartItemDebugDetails cartItemId={cartItemId} />
              </IfDebug>
              <DesignPreview cartItemId={cartItemId} />
            </Column>
          </Row>
        </GridContainer>
      </Column>
      <Column span={8} push={1} spanXs={9}>
        <GridContainer>
          <Row marginBottom="5">
            <Column span={12}>
              <H4
                fontSize="standard"
                fontWeight="bold"
                id={`product-name-${cartItemId}`}
              >
                {productName}
              </H4>
            </Column>
          </Row>
          <Row>
            <Column span={12}>
              <ModTarget
                hostModWrapper={ModWrapperWithPadding}
                name="itemInfo"
              />
            </Column>
          </Row>
          <Row marginBottom="5">
            <Column span={12}>
              <FlexBox
                marginTop="2"
                justifyContent="space-between"
                flexWrap="wrap"
                alignItems="baseline"
              >
                <ModTarget name="quantitySelection">
                  {/** Default quantity selector if there are no mods targeting this ModTarget. */}
                  <QuantitySelector
                    activeQuantity={quantity}
                    // Only pass merchandisable quantities if CartItem should support quantity update
                    merchandisableQuantities={
                      supportUpdateQuantity
                        ? merchandisableQuantities
                        : undefined
                    }
                    updateQuantityFn={onQuantityChange}
                    selectorDisabled={blockUserInteraction}
                    cartItemId={cartItemId}
                  />
                </ModTarget>
                {shouldDisplaySaveForLaterAction && (
                  <ConfirmationModal
                    linkText={cartMessages.saveForLater}
                    linkDisabled={blockUserInteraction}
                    prompt={cartMessages.saveForLaterWarningConfirmation}
                    cancelText={cartMessages.saveForLaterDecline}
                    confirmText={cartMessages.saveForLaterConfirm}
                    action={handleSaveForLaterClick}
                    visuallyHiddenModalDialogLabel={
                      removeItemVisuallyHiddenLabel
                    }
                    linkTextDescribedBy={`product-name-${cartItemId}`}
                    linkClickCallback={handleOpenSaveForLater}
                    cancelBtnCallback={handleCancelSaveForLater}
                  />
                )}
                {supportRemove && (
                  <ConfirmationModal
                    linkText={cartMessages.remove}
                    linkDisabled={blockUserInteraction}
                    prompt={removeItemWarning}
                    cancelText={cartMessages.removeItemDecline}
                    confirmText={cartMessages.removeItemConfirm}
                    action={removeItem}
                    visuallyHiddenModalDialogLabel={
                      removeItemVisuallyHiddenLabel
                    }
                    linkTextDescribedBy={`product-name-${cartItemId}`}
                  />
                )}
              </FlexBox>
            </Column>
          </Row>
          <Row>
            <Column span={12}>
              <CustomDataDisplay
                customDisplayAttributes={customDisplayAttributes}
              />
            </Column>
          </Row>
          <Row>
            <Column span={12}>
              <CartItemAttributeSummary
                cartItemId={cartItemId}
                pricingData={pricingData}
                productAttributes={productAttributes}
              />
            </Column>
          </Row>
          <Row>
            <ModTarget name="itemDetails" hostModWrapper={ModColumnWrapper} />
          </Row>
          <Row>
            <Column span={12}>
              <Divider marginY="5" />
            </Column>
          </Row>
          <Row>
            <Column span={12}>
              <ItemPricingSummary cartItemId={cartItemId} />
            </Column>
          </Row>
        </GridContainer>
      </Column>
    </Row>
  );
};

CartItem.propTypes = {
  cartItemId: PropTypes.string.isRequired,
  supportedActions: PropTypes.objectOf({
    remove: PropTypes.bool,
    changeQuantity: PropTypes.bool,
  }),
};
